import React, { useState } from "react";
import { ArrowDown, ArrowUp, GripVertical, ListOrdered } from "lucide-react";
import { ModuleBadge } from "layouts/SidebarBadge";
import { getModuleIcon } from "utils/module-icon";
import { groupKey } from "../features/sidebar-order";

// Sidebar Order tab of the Module Access dialog: the role's assigned modules in
// the order its sidebar shows them. A row moves by drag and drop or by its
// arrow buttons, only within its own group (top-level modules, or the
// sub-modules under one parent), so a parent always moves with its sub-modules.

const ArrowButton = ({ label, disabled, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className="flex h-7 w-7 items-center justify-center rounded-md border border-border bg-surface text-text_secondary hover:text-text_primary disabled:cursor-not-allowed disabled:opacity-40"
  >
    {children}
  </button>
);

const OrderRow = ({ module, number, isChild, isFirst, isLast, isMoved, isDragging, isDropTarget, editable, disabled, dragProps, onMoveBy }) => {
  const Icon = getModuleIcon(module.icon);

  return (
    <div
      {...dragProps}
      className={`grid grid-cols-[24px_44px_minmax(0,1fr)_auto] items-center gap-1 border-b border-border px-3 py-2
        ${isChild ? "bg-surface" : ""} ${isDropTarget ? "bg-primary_tint" : ""} ${isDragging ? "opacity-40" : ""}
        ${editable && !disabled ? "cursor-grab active:cursor-grabbing" : ""}`}
    >
      <span className="flex justify-center text-text_muted">{editable && <GripVertical size={14} />}</span>

      <div className="flex items-center gap-1.5 text-[11px] tabular-nums text-text_muted">
        <span
          className={`h-1.5 w-1.5 shrink-0 rounded-full ${isMoved ? "bg-warning" : "bg-transparent"}`}
          title={isMoved ? "Unsaved change" : undefined}
        />
        {number}
      </div>

      <div className={`flex min-w-0 items-center gap-2 ${isChild ? "relative pl-7" : ""}`}>
        {isChild && <span className="absolute -bottom-2 -top-2 left-[9px] w-px bg-border" aria-hidden="true" />}
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-primary bg-primary_tint text-primary">
          <Icon size={14} />
        </div>
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-[13px] font-medium text-text_primary">{module.name}</span>
            {module.badge && <ModuleBadge badge={module.badge} />}
            {!module.is_activated && (
              <span
                className="shrink-0 rounded-sm border border-border px-1.5 py-px text-[9.5px] font-semibold uppercase tracking-wide text-text_muted"
                title="Hidden from every sidebar until it is activated in Module Master"
              >
                Inactive
              </span>
            )}
          </div>
          {module.path && module.path !== "#" && (
            <div className="truncate font-mono text-[11px] text-text_muted" title={module.path}>
              {module.path}
            </div>
          )}
        </div>
      </div>

      {editable && (
        <div className="flex items-center gap-1">
          <ArrowButton label={`Move ${module.name} up`} disabled={disabled || isFirst} onClick={() => onMoveBy(-1)}>
            <ArrowUp size={14} />
          </ArrowButton>
          <ArrowButton label={`Move ${module.name} down`} disabled={disabled || isLast} onClick={() => onMoveBy(1)}>
            <ArrowDown size={14} />
          </ArrowButton>
        </div>
      )}
    </div>
  );
};

// tree: [{ id, children: [id] }] in sidebar order; moved: Set of ids whose position is unsaved;
// onMove(groupKey, groupIds, fromIndex, toIndex).
const SidebarOrderTab = ({ tree, byId, moved, onMove, editable, disabled }) => {
  // The row being dragged ({ key, id }) and the row it would drop on.
  const [drag, setDrag] = useState(null);
  const [dropId, setDropId] = useState(null);

  if (!tree.length) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-6 py-16 text-center">
        <ListOrdered size={28} className="text-text_muted" />
        <p className="text-sm font-semibold text-text_primary">No modules assigned yet</p>
        <p className="max-w-sm text-xs text-text_secondary">
          Assign modules in the Access tab first. They show up here to put in order.
        </p>
      </div>
    );
  }

  const endDrag = () => {
    setDrag(null);
    setDropId(null);
  };

  const renderRow = (id, key, ids, position, number, isChild) => {
    const canDrag = editable && !disabled;
    const dragProps = canDrag
      ? {
          draggable: true,
          onDragStart: (e) => {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", String(id));
            setDrag({ key, id });
          },
          // Only rows of the dragged row's own group accept the drop.
          onDragOver: (e) => {
            if (drag?.key !== key) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            if (dropId !== id) setDropId(id);
          },
          onDrop: (e) => {
            e.preventDefault();
            if (drag?.key === key && drag.id !== id) onMove(key, ids, ids.indexOf(drag.id), position);
            endDrag();
          },
          onDragEnd: endDrag,
        }
      : {};

    return (
      <OrderRow
        key={id}
        module={byId[id]}
        number={number}
        isChild={isChild}
        isFirst={position === 0}
        isLast={position === ids.length - 1}
        isMoved={moved.has(id)}
        isDragging={drag?.id === id}
        isDropTarget={dropId === id && drag && drag.id !== id}
        editable={editable}
        disabled={disabled}
        dragProps={dragProps}
        onMoveBy={(step) => onMove(key, ids, position, position + step)}
      />
    );
  };

  const rootIds = tree.map((node) => node.id);

  return tree.map((node, i) => (
    <React.Fragment key={node.id}>
      {renderRow(node.id, "root", rootIds, i, i + 1, false)}
      {node.children.map((childId, j) =>
        renderRow(childId, groupKey(node.id), node.children, j, `${i + 1}.${j + 1}`, true)
      )}
    </React.Fragment>
  ));
};

export default SidebarOrderTab;
